// src/utils/api.js — API fetch helpers with auth headers and 401 handling
import { state } from '../state.js';
import { API_BASE, SESSION_TOKEN_KEY, LEGACY_TOKEN_KEY, USER_META_KEY } from './constants.js';

/**
 * Get stored session token from localStorage (primary key).
 */
function getStoredSessionTokenOnly() {
    try { return localStorage.getItem(SESSION_TOKEN_KEY) || null; } catch (e) { return null; }
}

/**
 * Get stored legacy token from localStorage (fallback key).
 */
export function getStoredLegacyToken() {
    try { return localStorage.getItem(LEGACY_TOKEN_KEY) || null; } catch (e) { return null; }
}

/**
 * Get any stored session token (primary or legacy).
 */
export function getStoredSessionToken() {
    return getStoredSessionTokenOnly() || getStoredLegacyToken();
}

/** Re-export the primary-only accessor for auth flow */
export { getStoredSessionTokenOnly };

/**
 * The one accessor every authenticated request must use.
 */
export function getSessionToken() {
    return state.idToken || getStoredSessionToken();
}

/**
 * Store a session token in localStorage and in-memory state.
 */
export function setSessionToken(token, options) {
    const legacy = !!(options && options.legacy);
    try {
        if (legacy) {
            localStorage.setItem(LEGACY_TOKEN_KEY, token);
        } else {
            localStorage.setItem(SESSION_TOKEN_KEY, token);
            localStorage.removeItem(LEGACY_TOKEN_KEY);
        }
    } catch (e) { /* storage disabled — in-memory token still works this session */ }
    state.idToken = token;
}

/**
 * Clear session token from localStorage and in-memory state.
 */
export function clearSessionToken() {
    try {
        localStorage.removeItem(SESSION_TOKEN_KEY);
        localStorage.removeItem(LEGACY_TOKEN_KEY);
    } catch (e) { /* storage disabled */ }
    state.idToken = null;
    clearUserMetaCache();
}

/**
 * Clear user metadata cache from localStorage.
 */
export function clearUserMetaCache() {
    try { localStorage.removeItem(USER_META_KEY); } catch (e) { /* noop */ }
}

/**
 * Build auth headers with bearer token and content-type.
 */
export function getAuthHeaders(extraHeaders = {}) {
    const headers = {
        'Content-Type': 'application/json',
        ...extraHeaders,
    };
    const token = getSessionToken();
    if (token) {
        headers['Authorization'] = 'Bearer ' + token;
    }
    return headers;
}

/**
 * Simple authenticated fetch wrapper.
 */
export function apiFetch(path, options = {}) {
    const headers = getAuthHeaders(options.headers || {});
    return fetch(API_BASE + path, { ...options, headers });
}
